import type { PrintLayoutConfig } from "../types/printer.types";
import { INDIA_TIME_ZONE } from "./client-platform";

export type InvoicePrintFormat =
  | "58MM"
  | "80MM"
  | "DOT_MATRIX"
  | "DOT_MATRIX_9_5X5_5"
  | "A5"
  | "A4";

export const FANFOLD_9_5X5_5_PAGE = {
  widthMm: 241.3,
  heightMm: 139.7,
  widthIn: 9.5,
  heightIn: 5.5,
} as const;

export const INVOICE_PRINT_SCHEMA_VERSION = "INVOICE_PRINT_V3" as const;

/** Audit value used when an operator requests another invoice copy. */
export const INVOICE_REPRINT_AUDIT_REASON = "REPRINT:OPERATOR_REQUESTED_COPY";

export interface InvoiceRenderingMetadata {
  schemaVersion: typeof INVOICE_PRINT_SCHEMA_VERSION;
  templateVersion: string;
  templateHash: string;
  printerId: string | null;
  printerName?: string | null;
  connectionType?: "SYSTEM" | "NETWORK" | "SERIAL" | "USB" | null;
  paperWidth?: number | null;
  paperHeight?: number | null;
  mediaProfile: InvoicePrintFormat;
  layoutConfig: PrintLayoutConfig;
}

export interface InvoicePrintDocument {
  saleId: string;
  invoiceNumber: string;
  acceptedAt: string;
  fiscalYear: string;
  store: {
    name: string;
    address: string;
    city: string;
    state: string;
    pincode: string;
    phone: string | null;
    whatsapp?: string | null;
    email?: string | null;
    gstin: string | null;
    drugLicense: string | null;
    fssaiLicense?: string | null;
  };
  customer: {
    name: string;
    phone: string | null;
    email?: string | null;
    address: string | null;
  } | null;
  compliance?: {
    patientName?: string | null;
    patientAddress?: string | null;
    doctorName?: string | null;
    doctorRegistration?: string | null;
    prescriptionNumber?: string | null;
    prescriptionDate?: string | null;
  } | null;
  cashierName: string;
  items: Array<{
    lineId: string;
    name: string;
    sku: string;
    hsnCode: string | null;
    manufacturer: string | null;
    scheduleType: string;
    warnings?: string[];
    quantity: string;
    /** Aggregate non-paid quantity retained for backward compatibility. */
    freeQuantity: string;
    promotionalFreeQuantity: string;
    replacementQuantity: string;
    sampleQuantity: string;
    unitMrpPaise: number;
    unitPricePaise: number;
    gstBps: number;
    grossPaise: number;
    discountPaise: number;
    taxablePaise: number;
    cgstPaise: number;
    sgstPaise: number;
    igstPaise: number;
    taxPaise: number;
    totalPaise: number;
    lots: Array<{
      batchNumber: string;
      expiryDate: string | null;
      quantity: string;
    }>;
  }>;
  charges?: Array<{
    lineId: string;
    chargeCode: string;
    name: string;
    lineKind: "DELIVERY" | "PACKING" | "HANDLING" | "OTHER_SERVICE";
    quantity: string;
    uqc: string;
    unitPricePaise: number;
    gstBps: number;
    grossPaise: number;
    discountPaise: number;
    taxablePaise: number;
    cgstPaise: number;
    sgstPaise: number;
    igstPaise: number;
    taxPaise: number;
    settlementAdjustmentPaise: number;
    totalPaise: number;
    refundable: boolean;
    refundPolicy: unknown;
  }>;
  kits?: Array<{
    lineId: string;
    kitCode: string;
    name: string;
    packIdentity: string;
    kitBatchNumber: string;
    kitExpiryDate: string;
    quantity: string;
    uqc: string;
    printedMrpPaise: number;
    unitPricePaise: number;
    gstBps: number;
    grossPaise: number;
    discountPaise: number;
    taxablePaise: number;
    cgstPaise: number;
    sgstPaise: number;
    igstPaise: number;
    taxPaise: number;
    settlementAdjustmentPaise: number;
    totalPaise: number;
    returnPolicy: unknown;
    components: Array<{
      productId: string;
      name: string;
      quantity: string;
      lots: Array<{
        batchNumber: string;
        expiryDate: string | null;
        quantity: string;
      }>;
    }>;
  }>;
  totals: {
    subtotalPaise: number;
    discountPaise: number;
    taxablePaise: number;
    cgstPaise: number;
    sgstPaise: number;
    igstPaise: number;
    taxPaise: number;
    roundOffPaise: number;
    payablePaise: number;
  };
  tenders: Array<{
    method: "CASH" | "CARD" | "UPI" | "LOYALTY" | "ADVANCE";
    amountPaise: number;
    reference: string | null;
  }>;
}

const THERMAL_COLUMNS = 48;

function money(paise: number, currency = "Rs.") {
  return `${currency} ${(paise / 100).toFixed(2)}`;
}

function quantity(value: string) {
  return value.replace(/(\.\d*?[1-9])0+$/, "$1").replace(/\.0+$/, "");
}

function quantityBreakdown(
  item: InvoicePrintDocument["items"][number],
): string {
  const buckets = [
    `Qty ${quantity(item.quantity)}`,
    ...(Number(item.promotionalFreeQuantity)
      ? [`promo ${quantity(item.promotionalFreeQuantity)}`]
      : []),
    ...(Number(item.replacementQuantity)
      ? [`replacement ${quantity(item.replacementQuantity)}`]
      : []),
    ...(Number(item.sampleQuantity)
      ? [`sample ${quantity(item.sampleQuantity)}`]
      : []),
  ];
  return buckets.join(" + ");
}

export function formatInvoiceDateTime(value: string) {
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
  const instant = new Date(value);
  if (Number.isNaN(instant.getTime())) return value;

  const parts = Object.fromEntries(
    new Intl.DateTimeFormat("en-US", {
      timeZone: INDIA_TIME_ZONE,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    })
      .formatToParts(instant)
      .map((part) => [part.type, part.value]),
  );
  return `${parts.year}-${parts.month}-${parts.day} ${parts.hour}:${parts.minute} ${parts.dayPeriod}`;
}

function expiry(value: string | null) {
  return value ? value.slice(0, 7) : "-";
}

function thermalLine(left: string, right = "") {
  if (!right) return left.slice(0, THERMAL_COLUMNS);
  const room = Math.max(1, THERMAL_COLUMNS - right.length);
  return `${left.slice(0, room).padEnd(room)}${right}`;
}

function escapeHtml(value: string | null | undefined) {
  return (value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function invoicePatient(document: InvoicePrintDocument) {
  return {
    name:
      document.compliance?.patientName ??
      document.customer?.name ??
      "Walk-in customer",
    phone: document.customer?.phone ?? null,
    address:
      document.compliance?.patientAddress ?? document.customer?.address ?? null,
  };
}

function invoiceDoctorInfoHtml(document: InvoicePrintDocument) {
  const compliance = document.compliance;
  if (!compliance) return "";

  const details = [
    compliance.doctorName
      ? `<div><strong>Doctor:</strong> ${escapeHtml(compliance.doctorName)}${compliance.doctorRegistration ? ` · Reg ${escapeHtml(compliance.doctorRegistration)}` : ""}</div>`
      : "",
    compliance.prescriptionNumber
      ? `<div><strong>Rx:</strong> ${escapeHtml(compliance.prescriptionNumber)}${compliance.prescriptionDate ? ` · ${escapeHtml(compliance.prescriptionDate)}` : ""}</div>`
      : "",
  ].filter(Boolean);

  return details.length
    ? `<strong>Doctor Info</strong>${details.join("")}`
    : "";
}

export function renderInvoice80mm(document: InvoicePrintDocument) {
  const patient = invoicePatient(document);
  const lines: string[] = [
    document.store.name.toUpperCase().slice(0, THERMAL_COLUMNS),
    `${document.store.address}, ${document.store.city} - ${document.store.pincode}`.slice(
      0,
      THERMAL_COLUMNS,
    ),
  ];
  if (document.store.phone) lines.push(`Tel: ${document.store.phone}`);
  if (document.store.gstin) lines.push(`GSTIN: ${document.store.gstin}`);
  if (document.store.drugLicense) {
    lines.push(`DL: ${document.store.drugLicense}`);
  }
  lines.push(
    "=".repeat(THERMAL_COLUMNS),
    "TAX INVOICE",
    thermalLine(
      `No: ${document.invoiceNumber}`,
      formatInvoiceDateTime(document.acceptedAt),
    ),
  );
  lines.push(`Patient: ${patient.name}`.slice(0, THERMAL_COLUMNS));
  if (patient.phone) lines.push(`Phone: ${patient.phone}`);
  if (patient.address)
    lines.push(`Address: ${patient.address}`.slice(0, THERMAL_COLUMNS));
  if (document.compliance?.doctorName) {
    lines.push(
      `Doctor: ${document.compliance.doctorName}${document.compliance.doctorRegistration ? ` · Reg ${document.compliance.doctorRegistration}` : ""}`.slice(
        0,
        THERMAL_COLUMNS,
      ),
    );
  }
  if (document.compliance?.prescriptionNumber) {
    lines.push(
      `Rx: ${document.compliance.prescriptionNumber}${document.compliance.prescriptionDate ? ` · ${document.compliance.prescriptionDate}` : ""}`.slice(
        0,
        THERMAL_COLUMNS,
      ),
    );
  }
  lines.push("-".repeat(THERMAL_COLUMNS));
  for (const item of document.items) {
    lines.push(item.name.slice(0, THERMAL_COLUMNS));
    const lots = item.lots
      .map(
        (lot) =>
          `${lot.batchNumber}/${expiry(lot.expiryDate)} x${quantity(lot.quantity)}`,
      )
      .join(", ");
    lines.push(`Batch/Exp: ${lots || "-"}`.slice(0, THERMAL_COLUMNS));
    lines.push(
      `Paid ${quantity(item.quantity)}${Number(item.promotionalFreeQuantity) ? ` | Promo ${quantity(item.promotionalFreeQuantity)}` : ""}`,
    );
    if (Number(item.replacementQuantity) || Number(item.sampleQuantity)) {
      lines.push(
        `${Number(item.replacementQuantity) ? `Replacement ${quantity(item.replacementQuantity)}` : ""}${Number(item.replacementQuantity) && Number(item.sampleQuantity) ? " | " : ""}${Number(item.sampleQuantity) ? `Sample ${quantity(item.sampleQuantity)}` : ""}`,
      );
    }
    lines.push(
      thermalLine(`Rate ${money(item.unitPricePaise)}`, money(item.totalPaise)),
    );
    lines.push(
      thermalLine(
        `MRP ${money(item.unitMrpPaise)} GST ${(item.gstBps / 100).toFixed(2)}%`,
        item.hsnCode ? `HSN ${item.hsnCode}` : "",
      ),
    );
  }
  for (const kit of document.kits ?? []) {
    lines.push(`${kit.name} (KIT)`.slice(0, THERMAL_COLUMNS));
    lines.push(
      thermalLine(
        `${quantity(kit.quantity)} ${kit.uqc} × ${money(kit.unitPricePaise)}`,
        money(kit.totalPaise),
      ),
    );
    lines.push(
      thermalLine(
        `MRP ${money(kit.printedMrpPaise)} GST ${(kit.gstBps / 100).toFixed(2)}%`,
        kit.packIdentity,
      ),
    );
    lines.push(
      `Kit batch/exp: ${kit.kitBatchNumber}/${expiry(kit.kitExpiryDate)}`.slice(
        0,
        THERMAL_COLUMNS,
      ),
    );
    for (const component of kit.components) {
      const lots = component.lots
        .map(
          (lot) =>
            `${lot.batchNumber}/${expiry(lot.expiryDate)} x${quantity(lot.quantity)}`,
        )
        .join(", ");
      lines.push(
        `  ${component.name} x${quantity(component.quantity)} · ${lots || "-"}`.slice(
          0,
          THERMAL_COLUMNS,
        ),
      );
    }
  }
  for (const charge of document.charges ?? []) {
    lines.push(`${charge.name} (${charge.lineKind})`.slice(0, THERMAL_COLUMNS));
    lines.push(
      thermalLine(
        `${quantity(charge.quantity)} ${charge.uqc} × ${money(charge.unitPricePaise)}`,
        money(charge.totalPaise),
      ),
    );
    lines.push(`GST ${(charge.gstBps / 100).toFixed(2)}% · Service charge`);
  }
  lines.push(
    "-".repeat(THERMAL_COLUMNS),
    thermalLine("Subtotal", money(document.totals.subtotalPaise)),
  );
  if (document.totals.discountPaise) {
    lines.push(
      thermalLine("Discount", `-${money(document.totals.discountPaise)}`),
    );
  }
  if (document.totals.cgstPaise) {
    lines.push(thermalLine("CGST", money(document.totals.cgstPaise)));
  }
  if (document.totals.sgstPaise) {
    lines.push(thermalLine("SGST", money(document.totals.sgstPaise)));
  }
  if (document.totals.igstPaise) {
    lines.push(thermalLine("IGST", money(document.totals.igstPaise)));
  }
  if (document.totals.roundOffPaise) {
    lines.push(thermalLine("Round-off", money(document.totals.roundOffPaise)));
  }
  lines.push(
    "=".repeat(THERMAL_COLUMNS),
    thermalLine("TOTAL", money(document.totals.payablePaise)),
    "=".repeat(THERMAL_COLUMNS),
  );
  for (const tender of document.tenders) {
    lines.push(
      thermalLine(
        `Tender ${tender.method}${tender.reference ? ` (${tender.reference})` : ""}`,
        money(tender.amountPaise),
      ),
    );
  }
  lines.push("Thank you. Get well soon!");
  return `${lines.map((line) => line.trimEnd()).join("\n")}\n`;
}

export function renderInvoiceA4(document: InvoicePrintDocument) {
  const patient = invoicePatient(document);
  const itemRows = document.items
    .map((item, index) => {
      const lots = item.lots
        .map(
          (lot) => `${escapeHtml(lot.batchNumber)} / ${expiry(lot.expiryDate)}`,
        )
        .join("<br>");
      return `<tr><td>${index + 1}</td><td><strong>${escapeHtml(item.name)}</strong><br><small>${escapeHtml(item.manufacturer)} · SKU ${escapeHtml(item.sku)}</small></td><td>${escapeHtml(item.hsnCode) || "-"}</td><td>${lots || "-"}</td><td class="num">${quantityBreakdown(item)}</td><td class="num">${money(item.unitMrpPaise, "₹")}</td><td class="num">${money(item.unitPricePaise, "₹")}</td><td class="num">${(item.gstBps / 100).toFixed(2)}%</td><td class="num">${money(item.totalPaise, "₹")}</td></tr>`;
    })
    .join("");
  const kitRows = (document.kits ?? [])
    .map((kit, index) => {
      const componentTrace = kit.components
        .map((component) => {
          const lots = component.lots
            .map(
              (lot) =>
                `${escapeHtml(lot.batchNumber)} / ${expiry(lot.expiryDate)}`,
            )
            .join(", ");
          return `${escapeHtml(component.name)} × ${quantity(component.quantity)} (${lots || "-"})`;
        })
        .join("<br>");
      return `<tr><td>${document.items.length + index + 1}</td><td><strong>${escapeHtml(kit.name)}</strong><br><small>Commercial kit · ${escapeHtml(kit.kitCode)} · ${escapeHtml(kit.packIdentity)}</small></td><td>-</td><td>${escapeHtml(kit.kitBatchNumber)} / ${expiry(kit.kitExpiryDate)}<br><small>${componentTrace}</small></td><td class="num">${quantity(kit.quantity)} ${escapeHtml(kit.uqc)}</td><td class="num">${money(kit.printedMrpPaise, "₹")}</td><td class="num">${money(kit.unitPricePaise, "₹")}</td><td class="num">${(kit.gstBps / 100).toFixed(2)}%</td><td class="num">${money(kit.totalPaise, "₹")}</td></tr>`;
    })
    .join("");
  const chargeRows = (document.charges ?? [])
    .map(
      (charge, index) =>
        `<tr><td>${document.items.length + (document.kits?.length ?? 0) + index + 1}</td><td><strong>${escapeHtml(charge.name)}</strong><br><small>${escapeHtml(charge.lineKind)} · ${escapeHtml(charge.chargeCode)}</small></td><td>-</td><td>-</td><td class="num">${quantity(charge.quantity)} ${escapeHtml(charge.uqc)}</td><td class="num">-</td><td class="num">${money(charge.unitPricePaise, "₹")}</td><td class="num">${(charge.gstBps / 100).toFixed(2)}%</td><td class="num">${money(charge.totalPaise, "₹")}</td></tr>`,
    )
    .join("");
  const tenderRows = document.tenders
    .map(
      (tender) =>
        `<div><span>${tender.method}${tender.reference ? ` (${escapeHtml(tender.reference)})` : ""}</span><strong>${money(tender.amountPaise, "₹")}</strong></div>`,
    )
    .join("");
  const roundOffRow = document.totals.roundOffPaise
    ? `<div><span>Round-off</span><strong>${money(document.totals.roundOffPaise, "₹")}</strong></div>`
    : "";
  const doctorInfo = invoiceDoctorInfoHtml(document);
  const metaSections = [
    `<div class="box"><strong>TAX INVOICE</strong><br>No: ${escapeHtml(document.invoiceNumber)}<br>Date: ${formatInvoiceDateTime(document.acceptedAt)}<br>FY: ${escapeHtml(document.fiscalYear)}</div>`,
    `<div class="box"><strong>Patient</strong><br>${escapeHtml(patient.name)}<br>${escapeHtml(patient.phone)}<br>${escapeHtml(patient.address)}</div>`,
    ...(doctorInfo ? [`<div class="box doctor-info">${doctorInfo}</div>`] : []),
  ];
  return `<!doctype html><html><head><meta charset="utf-8"><title>Tax Invoice ${escapeHtml(document.invoiceNumber)}</title><style>@page{size:A4;margin:12mm}*{box-sizing:border-box}body{font:12px/1.2 Arial,sans-serif;color:#111;margin:0}header{text-align:center;border-bottom:2px solid #111;padding-bottom:4px}h1{font-size:20px;margin:0 0 2px}.meta,.party,.totals{display:grid;gap:4px;margin:5px 0}.meta{grid-template-columns:repeat(3,minmax(0,1fr))}.meta.columns-2,.party,.totals{grid-template-columns:repeat(2,minmax(0,1fr))}.meta.columns-1{grid-template-columns:1fr}.box{min-width:0;border:1px solid #bbb;padding:4px}table{width:100%;border-collapse:collapse;margin:5px 0}th,td{border:1px solid #999;padding:3px;vertical-align:top}th{background:#eee;text-align:left}.num{text-align:right;white-space:nowrap}.summary{margin-left:auto;width:310px}.summary div,.tenders div{display:flex;justify-content:space-between;padding:1px 0}.grand{border-top:2px solid #111;font-size:15px;margin-top:2px;padding-top:3px!important}footer{margin-top:6px;border-top:1px solid #aaa;padding-top:4px;text-align:center}</style></head><body><header><h1>${escapeHtml(document.store.name)}</h1><div>${escapeHtml(document.store.address)}, ${escapeHtml(document.store.city)}, ${escapeHtml(document.store.state)} - ${escapeHtml(document.store.pincode)}</div><div>${document.store.gstin ? `GSTIN: ${escapeHtml(document.store.gstin)}` : ""}${document.store.drugLicense ? ` · DL: ${escapeHtml(document.store.drugLicense)}` : ""}${document.store.phone ? ` · Tel: ${escapeHtml(document.store.phone)}` : ""}</div></header><section class="meta columns-${metaSections.length}">${metaSections.join("")}</section><table><thead><tr><th>#</th><th>Product / Service</th><th>HSN</th><th>Batch / Exp</th><th>Qty</th><th>MRP</th><th>Rate</th><th>GST</th><th>Amount</th></tr></thead><tbody>${itemRows}${kitRows}${chargeRows}</tbody></table><section class="totals"><div class="box tenders"><strong>Tenders</strong>${tenderRows}</div><div class="box summary"><div><span>Subtotal</span><strong>${money(document.totals.subtotalPaise, "₹")}</strong></div><div><span>Discount</span><strong>-${money(document.totals.discountPaise, "₹")}</strong></div><div><span>Taxable</span><strong>${money(document.totals.taxablePaise, "₹")}</strong></div><div><span>CGST</span><strong>${money(document.totals.cgstPaise, "₹")}</strong></div><div><span>SGST</span><strong>${money(document.totals.sgstPaise, "₹")}</strong></div><div><span>IGST</span><strong>${money(document.totals.igstPaise, "₹")}</strong></div>${roundOffRow}<div class="grand"><span>TOTAL</span><strong>${money(document.totals.payablePaise, "₹")}</strong></div></div></section><footer>Thank you. Get well soon!</footer></body></html>`;
}

const TEMPLATE_BOOLEAN_KEYS = [
  "showHeader",
  "showStoreIdentity",
  "showStoreContact",
  "showStoreLicenses",
  "showFooter",
  "showTenders",
  "showLogo",
  "compactMode",
  "showGST",
  "showDoctor",
  "showHSN",
  "showBatch",
  "showMfr",
  "showSKU",
  "showSchedule",
  "showKitIdentity",
  "showKitComponents",
  "showChargeDetails",
  "showQuantity",
  "showMRP",
  "showExpiry",
  "showDiscount",
  "showTaxBreakdown",
  "showPatientInfo",
] as const;

export function defaultInvoiceLayout(
  format: InvoicePrintFormat,
): PrintLayoutConfig {
  const thermal = format === "58MM" || format === "80MM";
  const narrow = format === "58MM";
  const a5 = format === "A5";
  const fanfold = format === "DOT_MATRIX_9_5X5_5";
  return {
    fontSize: narrow ? 8 : fanfold ? 8 : thermal || a5 ? 9 : 11,
    fontFamily:
      thermal || format === "DOT_MATRIX" || fanfold
        ? "monospace"
        : "sans-serif",
    lineSpacing: narrow ? 1.1 : thermal ? 1.2 : fanfold ? 1.15 : a5 ? 1.3 : 1.4,
    margins: narrow
      ? { top: 2, right: 2, bottom: 2, left: 2 }
      : thermal
        ? { top: 3, right: 3, bottom: 3, left: 3 }
        : fanfold
          ? { top: 5, right: 12.7, bottom: 5, left: 12.7 }
          : a5
            ? { top: 10, right: 10, bottom: 10, left: 10 }
            : { top: 15, right: 15, bottom: 15, left: 15 },
    showHeader: true,
    showStoreIdentity: true,
    showStoreContact: true,
    showStoreLicenses: true,
    showFooter: true,
    showTenders: false,
    // Logos need embedded binary assets; keep this disabled until a versioned
    // logo snapshot is part of the canonical document.
    showLogo: false,
    compactMode: thermal || fanfold,
    showGST: true,
    showDoctor: !narrow,
    showHSN: !thermal,
    showBatch: true,
    showMfr: false,
    showSKU: false,
    showSchedule: false,
    showKitIdentity: false,
    showKitComponents: false,
    showChargeDetails: false,
    showQuantity: true,
    showMRP: true,
    showExpiry: true,
    showDiscount: true,
    showTaxBreakdown: !thermal,
    showPatientInfo: true,
    maxItemColumns: narrow ? 4 : thermal ? 6 : fanfold ? 10 : a5 ? 10 : 15,
    headerLines: [
      "{{storeName}}",
      `{{storeAddress}}, {{city}}${fanfold ? "" : ", {{state}}"} - {{pincode}}`,
    ],
    footerLines: fanfold
      ? [
          "E. & O.E.",
          "Goods once sold will not be taken back or exchanged.",
          "Subject to {{city}} jurisdiction.",
        ]
      : ["Thank you! Visit again."],
  };
}

export function normalizeInvoiceLayout(
  format: InvoicePrintFormat,
  layout?: Partial<PrintLayoutConfig> | null,
): PrintLayoutConfig {
  const preset = defaultInvoiceLayout(format);
  if (!layout) return preset;
  const normalized: PrintLayoutConfig = {
    ...preset,
    margins: { ...preset.margins, ...(layout.margins ?? {}) },
    headerLines: layout.headerLines
      ? [...layout.headerLines]
      : [...preset.headerLines],
    footerLines: layout.footerLines
      ? [...layout.footerLines]
      : [...preset.footerLines],
  };
  for (const key of TEMPLATE_BOOLEAN_KEYS) {
    if (typeof layout[key] === "boolean") normalized[key] = layout[key];
  }
  if (typeof layout.fontSize === "number")
    normalized.fontSize = layout.fontSize;
  if (layout.fontFamily) normalized.fontFamily = layout.fontFamily;
  if (typeof layout.lineSpacing === "number")
    normalized.lineSpacing = layout.lineSpacing;
  if (typeof layout.maxItemColumns === "number")
    normalized.maxItemColumns = layout.maxItemColumns;
  return normalized;
}

export function invoiceFormatForPrinterType(
  printerType?: string | null,
): InvoicePrintFormat {
  switch (printerType) {
    case "THERMAL_58MM":
      return "58MM";
    case "THERMAL_80MM":
      return "80MM";
    case "DOT_MATRIX":
      return "DOT_MATRIX";
    case "DOT_MATRIX_9_5X5_5":
      return "DOT_MATRIX_9_5X5_5";
    case "LASER_A5":
      return "A5";
    case "LASER_A4":
      return "A4";
    default:
      return "DOT_MATRIX_9_5X5_5";
  }
}

function templateValues(document: InvoicePrintDocument) {
  return {
    storeName: document.store.name,
    storeAddress: document.store.address,
    city: document.store.city,
    state: document.store.state,
    pincode: document.store.pincode,
    storePhone: document.store.phone ?? "",
    storeWhatsapp: document.store.whatsapp ?? "",
    storeEmail: document.store.email ?? "",
    gstin: document.store.gstin ?? "",
    dlNo: document.store.drugLicense ?? "",
    fssaiNo: document.store.fssaiLicense ?? "",
    invoiceNumber: document.invoiceNumber,
    fiscalYear: document.fiscalYear,
    customerName: document.customer?.name ?? "Walk-in customer",
    patientName:
      document.compliance?.patientName ??
      document.customer?.name ??
      "Walk-in customer",
    doctorName: document.compliance?.doctorName ?? "",
    doctorRegistration: document.compliance?.doctorRegistration ?? "",
    prescriptionNumber: document.compliance?.prescriptionNumber ?? "",
  };
}

function interpolateTemplateLine(line: string, document: InvoicePrintDocument) {
  const values = templateValues(document);
  return line
    .replace(/{{\s*([a-zA-Z0-9]+)\s*}}/g, (_match, key: string) =>
      key in values ? values[key as keyof typeof values] : "",
    )
    .replace(/\s+\|\s*(?=\||$)/g, "")
    .replace(/\|\s*$/g, "")
    .trim();
}

function invoiceHeaderSections(
  document: InvoicePrintDocument,
  layout: PrintLayoutConfig,
) {
  const identity = layout.headerLines
    .map((line) => interpolateTemplateLine(line, document))
    .filter(Boolean)
    .map((line, index) =>
      index === 0
        ? `<h1>${escapeHtml(line)}</h1>`
        : `<div>${escapeHtml(line)}</div>`,
    )
    .join("");
  const contact = [
    document.store.phone
      ? `<div><strong>Phone:</strong> ${escapeHtml(document.store.phone)}</div>`
      : "",
    document.store.whatsapp
      ? `<div><strong>WhatsApp:</strong> ${escapeHtml(document.store.whatsapp)}</div>`
      : "",
    document.store.email
      ? `<div><strong>Email:</strong> ${escapeHtml(document.store.email)}</div>`
      : "",
  ]
    .filter(Boolean)
    .join("");
  const licenses = [
    layout.showGST && document.store.gstin
      ? `<div><strong>GSTIN:</strong> ${escapeHtml(document.store.gstin)}</div>`
      : "",
    document.store.drugLicense
      ? `<div><strong>DL No:</strong> ${escapeHtml(document.store.drugLicense)}</div>`
      : "",
    document.store.fssaiLicense
      ? `<div><strong>FSSAI:</strong> ${escapeHtml(document.store.fssaiLicense)}</div>`
      : "",
  ]
    .filter(Boolean)
    .join("");

  return [
    ...(layout.showStoreIdentity && identity
      ? [{ className: "store", content: identity }]
      : []),
    ...(layout.showStoreContact && contact
      ? [{ className: "contact", content: contact }]
      : []),
    ...(layout.showStoreLicenses && licenses
      ? [{ className: "regulatory", content: licenses }]
      : []),
  ];
}

function invoiceHeaderHtml(
  document: InvoicePrintDocument,
  layout: PrintLayoutConfig,
) {
  if (!layout.showHeader) return "";
  const sections = invoiceHeaderSections(document, layout);
  if (!sections.length) return "";
  return `<header class="form-header columns-${sections.length}">${sections
    .map(
      (section) => `<div class="${section.className}">${section.content}</div>`,
    )
    .join("")}</header>`;
}

function clipText(value: string, columns: number) {
  return Array.from(value).slice(0, columns).join("");
}

function thermalLineFor(left: string, right: string, columns: number) {
  if (!right) return clipText(left, columns);
  const rightText = clipText(right, columns - 1);
  const room = Math.max(1, columns - Array.from(rightText).length);
  return `${clipText(left, room).padEnd(room)}${rightText}`;
}

function configuredThermalInvoice(
  document: InvoicePrintDocument,
  format: "58MM" | "80MM",
  layout: PrintLayoutConfig,
) {
  const patient = invoicePatient(document);
  const columns = format === "58MM" ? 32 : 48;
  const lines: string[] = [];
  if (layout.showHeader) {
    if (layout.showStoreIdentity) {
      for (const configured of layout.headerLines) {
        const line = interpolateTemplateLine(configured, document);
        if (line) lines.push(clipText(line, columns));
      }
    }
    if (layout.showStoreContact) {
      if (document.store.phone)
        lines.push(clipText(`Phone: ${document.store.phone}`, columns));
      if (document.store.whatsapp)
        lines.push(clipText(`WhatsApp: ${document.store.whatsapp}`, columns));
      if (document.store.email)
        lines.push(clipText(`Email: ${document.store.email}`, columns));
    }
    if (layout.showStoreLicenses) {
      if (layout.showGST && document.store.gstin)
        lines.push(clipText(`GSTIN: ${document.store.gstin}`, columns));
      if (document.store.drugLicense)
        lines.push(clipText(`DL No: ${document.store.drugLicense}`, columns));
      if (document.store.fssaiLicense)
        lines.push(clipText(`FSSAI: ${document.store.fssaiLicense}`, columns));
    }
  }
  lines.push(
    "=".repeat(columns),
    "TAX INVOICE",
    thermalLineFor(
      `No: ${document.invoiceNumber}`,
      formatInvoiceDateTime(document.acceptedAt),
      columns,
    ),
  );
  if (layout.showPatientInfo) {
    lines.push(clipText(`Patient: ${patient.name}`, columns));
    if (patient.phone) lines.push(clipText(`Phone: ${patient.phone}`, columns));
    if (patient.address)
      lines.push(clipText(`Address: ${patient.address}`, columns));
  }
  lines.push("-".repeat(columns));
  for (const item of document.items) {
    lines.push(clipText(item.name, columns));
    if (layout.showMfr && item.manufacturer)
      lines.push(clipText(`Mfr: ${item.manufacturer}`, columns));
    if (layout.showSKU) lines.push(clipText(`SKU: ${item.sku}`, columns));
    if (layout.showBatch) {
      const lots = item.lots
        .map((lot) => {
          const expiryPart = layout.showExpiry
            ? `/${expiry(lot.expiryDate)}`
            : "";
          return `${lot.batchNumber}${expiryPart}`;
        })
        .join(", ");
      lines.push(
        clipText(
          `Batch${layout.showExpiry ? "/Exp" : ""}: ${lots || "-"}`,
          columns,
        ),
      );
    }
    if (layout.showQuantity) {
      lines.push(
        clipText(
          `Paid ${quantity(item.quantity)}${Number(item.promotionalFreeQuantity) ? ` | Promo ${quantity(item.promotionalFreeQuantity)}` : ""}`,
          columns,
        ),
      );
      if (Number(item.replacementQuantity) || Number(item.sampleQuantity)) {
        lines.push(
          clipText(
            `${Number(item.replacementQuantity) ? `Replacement ${quantity(item.replacementQuantity)}` : ""}${Number(item.replacementQuantity) && Number(item.sampleQuantity) ? " | " : ""}${Number(item.sampleQuantity) ? `Sample ${quantity(item.sampleQuantity)}` : ""}`,
            columns,
          ),
        );
      }
    }
    lines.push(
      thermalLineFor(
        `Rate ${money(item.unitPricePaise)}`,
        money(item.totalPaise),
        columns,
      ),
    );
    const detail = [
      ...(layout.showMRP ? [`MRP ${money(item.unitMrpPaise)}`] : []),
      ...(layout.showGST ? [`GST ${(item.gstBps / 100).toFixed(2)}%`] : []),
      ...(layout.showHSN && item.hsnCode ? [`HSN ${item.hsnCode}`] : []),
      ...(layout.showSchedule && item.scheduleType ? [item.scheduleType] : []),
    ].join(" · ");
    if (detail) lines.push(clipText(detail, columns));
  }
  for (const kit of document.kits ?? []) {
    lines.push(clipText(`${kit.name} (KIT)`, columns));
    if (layout.showKitIdentity) {
      lines.push(
        clipText(`Kit: ${kit.kitCode} · ${kit.packIdentity}`, columns),
      );
    }
    lines.push(
      thermalLineFor(
        layout.showQuantity
          ? `${quantity(kit.quantity)} ${kit.uqc} x ${money(kit.unitPricePaise)}`
          : `Rate ${money(kit.unitPricePaise)}`,
        money(kit.totalPaise),
        columns,
      ),
    );
    if (layout.showBatch) {
      lines.push(
        clipText(
          `Kit batch${layout.showExpiry ? "/exp" : ""}: ${kit.kitBatchNumber}${layout.showExpiry ? `/${expiry(kit.kitExpiryDate)}` : ""}`,
          columns,
        ),
      );
    }
    if (layout.showKitComponents) {
      for (const component of kit.components) {
        const lots = layout.showBatch
          ? component.lots
              .map(
                (lot) =>
                  `${lot.batchNumber}${layout.showExpiry ? `/${expiry(lot.expiryDate)}` : ""}`,
              )
              .join(", ")
          : "";
        lines.push(
          clipText(
            `  ${component.name} x${quantity(component.quantity)}${lots ? ` · ${lots}` : ""}`,
            columns,
          ),
        );
      }
    }
  }
  for (const charge of document.charges ?? []) {
    lines.push(
      clipText(
        `${charge.name}${layout.showChargeDetails ? ` (${charge.lineKind} · ${charge.chargeCode})` : ""}`,
        columns,
      ),
    );
    lines.push(
      thermalLineFor(
        layout.showQuantity
          ? `${quantity(charge.quantity)} ${charge.uqc} x ${money(charge.unitPricePaise)}`
          : `Rate ${money(charge.unitPricePaise)}`,
        money(charge.totalPaise),
        columns,
      ),
    );
  }
  lines.push(
    "-".repeat(columns),
    thermalLineFor("Subtotal", money(document.totals.subtotalPaise), columns),
  );
  if (layout.showDiscount && document.totals.discountPaise) {
    lines.push(
      thermalLineFor(
        "Discount",
        `-${money(document.totals.discountPaise)}`,
        columns,
      ),
    );
  }
  if (layout.showTaxBreakdown) {
    if (document.totals.cgstPaise)
      lines.push(
        thermalLineFor("CGST", money(document.totals.cgstPaise), columns),
      );
    if (document.totals.sgstPaise)
      lines.push(
        thermalLineFor("SGST", money(document.totals.sgstPaise), columns),
      );
    if (document.totals.igstPaise)
      lines.push(
        thermalLineFor("IGST", money(document.totals.igstPaise), columns),
      );
  } else if (layout.showGST && document.totals.taxPaise) {
    lines.push(thermalLineFor("GST", money(document.totals.taxPaise), columns));
  }
  if (document.totals.roundOffPaise) {
    lines.push(
      thermalLineFor(
        "Round-off",
        money(document.totals.roundOffPaise),
        columns,
      ),
    );
  }
  lines.push(
    "=".repeat(columns),
    thermalLineFor("TOTAL", money(document.totals.payablePaise), columns),
    "=".repeat(columns),
  );
  if (layout.showDoctor && document.compliance) {
    lines.push("Prescription");
    const doctor = [
      document.compliance.doctorName,
      document.compliance.doctorRegistration
        ? `Reg ${document.compliance.doctorRegistration}`
        : null,
    ]
      .filter(Boolean)
      .join(" · ");
    if (doctor) lines.push(clipText(`Doctor: ${doctor}`, columns));
    if (document.compliance.prescriptionNumber)
      lines.push(
        clipText(
          `Rx: ${document.compliance.prescriptionNumber}${document.compliance.prescriptionDate ? ` · ${document.compliance.prescriptionDate}` : ""}`,
          columns,
        ),
      );
  }
  if (layout.showTenders) {
    for (const tender of document.tenders) {
      lines.push(
        thermalLineFor(
          `Tender ${tender.method}${tender.reference ? ` (${tender.reference})` : ""}`,
          money(tender.amountPaise),
          columns,
        ),
      );
    }
  }
  if (layout.showFooter) {
    for (const configured of layout.footerLines) {
      const line = interpolateTemplateLine(configured, document);
      if (line) lines.push(clipText(line, columns));
    }
  }
  return `${lines.map((line) => line.trimEnd()).join("\n")}\n`;
}

function configuredPageInvoice(
  document: InvoicePrintDocument,
  format: "DOT_MATRIX" | "A5" | "A4",
  layout: PrintLayoutConfig,
) {
  const patient = invoicePatient(document);
  let rowNumber = 0;
  const itemRows = document.items
    .map((item) => {
      rowNumber += 1;
      const lots = layout.showBatch
        ? item.lots
            .map(
              (lot) =>
                `${escapeHtml(lot.batchNumber)}${layout.showExpiry ? ` / ${expiry(lot.expiryDate)}` : ""}`,
            )
            .join("<br>")
        : "-";
      const details = [
        ...(layout.showMfr && item.manufacturer
          ? [escapeHtml(item.manufacturer)]
          : []),
        ...(layout.showSKU ? [`SKU ${escapeHtml(item.sku)}`] : []),
        ...(layout.showSchedule ? [escapeHtml(item.scheduleType)] : []),
      ].join(" · ");
      return `<tr><td>${rowNumber}</td><td><strong>${escapeHtml(item.name)}</strong>${details ? `<br><small>${details}</small>` : ""}</td>${layout.showHSN ? `<td>${escapeHtml(item.hsnCode) || "-"}</td>` : ""}${layout.showBatch ? `<td>${lots}</td>` : ""}${layout.showQuantity ? `<td class="num">${quantityBreakdown(item)}</td>` : ""}${layout.showMRP ? `<td class="num">${money(item.unitMrpPaise, "₹")}</td>` : ""}<td class="num">${money(item.unitPricePaise, "₹")}</td>${layout.showGST ? `<td class="num">${(item.gstBps / 100).toFixed(2)}%</td>` : ""}<td class="num">${money(item.totalPaise, "₹")}</td></tr>`;
    })
    .join("");
  const kitRows = (document.kits ?? [])
    .map((kit) => {
      rowNumber += 1;
      const components = layout.showKitComponents
        ? kit.components
            .map((component) => {
              const lots = layout.showBatch
                ? component.lots
                    .map(
                      (lot) =>
                        `${escapeHtml(lot.batchNumber)}${layout.showExpiry ? `/${expiry(lot.expiryDate)}` : ""}`,
                    )
                    .join(", ")
                : "";
              return `${escapeHtml(component.name)} × ${quantity(component.quantity)}${lots ? ` (${lots})` : ""}`;
            })
            .join("<br>")
        : "";
      const batch = layout.showBatch
        ? `${escapeHtml(kit.kitBatchNumber)}${layout.showExpiry ? ` / ${expiry(kit.kitExpiryDate)}` : ""}${components ? `<br><small>${components}</small>` : ""}`
        : "-";
      const identity = layout.showKitIdentity
        ? `Commercial kit · ${escapeHtml(kit.kitCode)} · ${escapeHtml(kit.packIdentity)}`
        : "";
      return `<tr><td>${rowNumber}</td><td><strong>${escapeHtml(kit.name)}</strong>${identity ? `<br><small>${identity}</small>` : ""}</td>${layout.showHSN ? "<td>-</td>" : ""}${layout.showBatch ? `<td>${batch}</td>` : ""}${layout.showQuantity ? `<td class="num">${quantity(kit.quantity)} ${escapeHtml(kit.uqc)}</td>` : ""}${layout.showMRP ? `<td class="num">${money(kit.printedMrpPaise, "₹")}</td>` : ""}<td class="num">${money(kit.unitPricePaise, "₹")}</td>${layout.showGST ? `<td class="num">${(kit.gstBps / 100).toFixed(2)}%</td>` : ""}<td class="num">${money(kit.totalPaise, "₹")}</td></tr>`;
    })
    .join("");
  const chargeRows = (document.charges ?? [])
    .map((charge) => {
      rowNumber += 1;
      const details = layout.showChargeDetails
        ? `<br><small>${escapeHtml(charge.lineKind)} · ${escapeHtml(charge.chargeCode)}</small>`
        : "";
      return `<tr><td>${rowNumber}</td><td><strong>${escapeHtml(charge.name)}</strong>${details}</td>${layout.showHSN ? "<td>-</td>" : ""}${layout.showBatch ? "<td>-</td>" : ""}${layout.showQuantity ? `<td class="num">${quantity(charge.quantity)} ${escapeHtml(charge.uqc)}</td>` : ""}${layout.showMRP ? '<td class="num">-</td>' : ""}<td class="num">${money(charge.unitPricePaise, "₹")}</td>${layout.showGST ? `<td class="num">${(charge.gstBps / 100).toFixed(2)}%</td>` : ""}<td class="num">${money(charge.totalPaise, "₹")}</td></tr>`;
    })
    .join("");
  const header = invoiceHeaderHtml(document, layout);
  const footerLines = layout.showFooter
    ? layout.footerLines
        .map((line) => interpolateTemplateLine(line, document))
        .filter(Boolean)
        .map((line) => `<div>${escapeHtml(line)}</div>`)
        .join("")
    : "";
  const tenderRows = layout.showTenders
    ? document.tenders
        .map(
          (tender) =>
            `<div><span>${tender.method}${tender.reference ? ` (${escapeHtml(tender.reference)})` : ""}</span><strong>${money(tender.amountPaise, "₹")}</strong></div>`,
        )
        .join("")
    : "";
  const taxRows = layout.showTaxBreakdown
    ? `<div><span>Taxable</span><strong>${money(document.totals.taxablePaise, "₹")}</strong></div><div><span>CGST</span><strong>${money(document.totals.cgstPaise, "₹")}</strong></div><div><span>SGST</span><strong>${money(document.totals.sgstPaise, "₹")}</strong></div><div><span>IGST</span><strong>${money(document.totals.igstPaise, "₹")}</strong></div>`
    : layout.showGST
      ? `<div><span>GST</span><strong>${money(document.totals.taxPaise, "₹")}</strong></div>`
      : "";
  const pageSize =
    format === "A5" ? "A5" : format === "DOT_MATRIX" ? "210mm 297mm" : "A4";
  const family =
    layout.fontFamily === "monospace"
      ? "ui-monospace,monospace"
      : layout.fontFamily === "serif"
        ? "Georgia,serif"
        : "Arial,sans-serif";
  const headerCells = `<th>#</th><th>Product / Service</th>${layout.showHSN ? "<th>HSN</th>" : ""}${layout.showBatch ? `<th>Batch${layout.showExpiry ? " / Exp" : ""}</th>` : ""}${layout.showQuantity ? "<th>Qty</th>" : ""}${layout.showMRP ? "<th>MRP</th>" : ""}<th>Rate</th>${layout.showGST ? "<th>GST</th>" : ""}<th>Amount</th>`;
  const doctorInfo = layout.showDoctor ? invoiceDoctorInfoHtml(document) : "";
  const metaSections = [
    `<div class="box"><strong>TAX INVOICE</strong><br>No: ${escapeHtml(document.invoiceNumber)}<br>Date: ${formatInvoiceDateTime(document.acceptedAt)}<br>FY: ${escapeHtml(document.fiscalYear)}</div>`,
    ...(layout.showPatientInfo
      ? [
          `<div class="box"><strong>Patient</strong><br>${escapeHtml(patient.name)}<br>${escapeHtml(patient.phone)}<br>${escapeHtml(patient.address)}</div>`,
        ]
      : []),
    ...(doctorInfo ? [`<div class="box doctor-info">${doctorInfo}</div>`] : []),
  ];
  const settlementDetails = layout.showTenders
    ? `<div class="tenders"><strong>Tenders</strong>${tenderRows}</div>`
    : "";
  return `<!doctype html><html><head><meta charset="utf-8"><title>Tax Invoice ${escapeHtml(document.invoiceNumber)}</title><style>
@page{size:${pageSize};margin:${layout.margins.top}mm ${layout.margins.right}mm ${layout.margins.bottom}mm ${layout.margins.left}mm}
*{box-sizing:border-box}
body{font:${layout.fontSize}px/${layout.lineSpacing} ${family};color:#111;margin:0}
.form-header{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));border-bottom:2px solid #111}
.form-header.columns-2{grid-template-columns:repeat(2,minmax(0,1fr))}
.form-header.columns-1{grid-template-columns:1fr}
.form-header>div{min-width:0;padding:3px 5px;border-right:1px solid #bbb}
.form-header>div:last-child{border-right:0}
.store{text-align:left}
.contact{text-align:center}
.regulatory{text-align:right}
.form-header.columns-1>div{text-align:center}
.form-header.columns-2>div:first-child{text-align:left}
.form-header.columns-2>div:last-child{text-align:right}
h1{font-size:${layout.fontSize * 1.7}px;margin:0 0 2px}
.meta,.totals{display:grid;gap:4px;margin:5px 0}
.meta{grid-template-columns:repeat(3,minmax(0,1fr))}
.meta.columns-2,.totals{grid-template-columns:repeat(2,minmax(0,1fr))}
.meta.columns-1,.totals.single{grid-template-columns:1fr}
.box{min-width:0;border:1px solid #bbb;padding:${layout.compactMode ? 3 : 4}px;overflow-wrap:anywhere}
table{width:100%;border-collapse:collapse;margin:5px 0}
th,td{border:1px solid #999;padding:${layout.compactMode ? 2 : 3}px;vertical-align:top}
th{background:#eee;text-align:left}
.num{text-align:right;white-space:nowrap}
.summary{margin-left:auto;width:100%;max-width:310px}
.summary div,.tenders>div{display:flex;justify-content:space-between;padding:1px 0}
.grand{border-top:2px solid #111;font-size:${layout.fontSize * 1.25}px;margin-top:2px;padding-top:3px!important}
footer{margin-top:6px;border-top:1px solid #aaa;padding-top:4px;text-align:center}
</style></head><body>${header}<section class="meta columns-${metaSections.length}">${metaSections.join("")}</section><table><thead><tr>${headerCells}</tr></thead><tbody>${itemRows}${kitRows}${chargeRows}</tbody></table><section class="totals${settlementDetails ? "" : " single"}">${settlementDetails ? `<div class="box settlement-details">${settlementDetails}</div>` : ""}<div class="box summary"><div><span>Subtotal</span><strong>${money(document.totals.subtotalPaise, "₹")}</strong></div>${layout.showDiscount ? `<div><span>Discount</span><strong>-${money(document.totals.discountPaise, "₹")}</strong></div>` : ""}${taxRows}${document.totals.roundOffPaise ? `<div><span>Round-off</span><strong>${money(document.totals.roundOffPaise, "₹")}</strong></div>` : ""}<div class="grand"><span>TOTAL</span><strong>${money(document.totals.payablePaise, "₹")}</strong></div></div></section>${layout.showFooter && footerLines ? `<footer>${footerLines}</footer>` : ""}</body></html>`;
}

function configuredFanfoldInvoice(
  document: InvoicePrintDocument,
  layout: PrintLayoutConfig,
  rendering?: InvoiceRenderingMetadata,
) {
  const patient = invoicePatient(document);
  let rowNumber = 0;
  const fanfoldQuantity = (item: InvoicePrintDocument["items"][number]) => {
    const extras = [
      ...(Number(item.promotionalFreeQuantity)
        ? [`P${quantity(item.promotionalFreeQuantity)}`]
        : []),
      ...(Number(item.replacementQuantity)
        ? [`R${quantity(item.replacementQuantity)}`]
        : []),
      ...(Number(item.sampleQuantity)
        ? [`S${quantity(item.sampleQuantity)}`]
        : []),
    ];
    return `${quantity(item.quantity)}${extras.length ? `<div class="qty-secondary">${extras.join(" ")}</div>` : ""}`;
  };
  const detailText = (item: InvoicePrintDocument["items"][number]) =>
    [
      ...(layout.showMfr && item.manufacturer
        ? [escapeHtml(item.manufacturer)]
        : []),
      ...(layout.showSKU ? [`SKU ${escapeHtml(item.sku)}`] : []),
      ...(layout.showSchedule && item.scheduleType
        ? [escapeHtml(item.scheduleType)]
        : []),
    ].join(" · ");
  const productRows = document.items
    .map((item) => {
      rowNumber += 1;
      const lots = layout.showBatch
        ? item.lots
            .map(
              (lot) =>
                `${escapeHtml(lot.batchNumber)}${layout.showExpiry ? ` / ${expiry(lot.expiryDate)}` : ""}`,
            )
            .join("<br>") || "-"
        : "";
      const details = detailText(item);
      return `<tr><td class="center">${rowNumber}</td><td class="description"><strong>${escapeHtml(item.name)}</strong>${details ? `<div class="secondary">${details}</div>` : ""}</td>${layout.showHSN ? `<td class="center">${escapeHtml(item.hsnCode) || "-"}</td>` : ""}${layout.showBatch ? `<td class="center">${lots}</td>` : ""}${layout.showQuantity ? `<td class="qty">${fanfoldQuantity(item)}</td>` : ""}${layout.showMRP ? `<td class="num">${money(item.unitMrpPaise, "₹")}</td>` : ""}<td class="num">${money(item.unitPricePaise, "₹")}</td>${layout.showGST ? `<td class="num">${(item.gstBps / 100).toFixed(2)}%</td>` : ""}<td class="num amount">${money(item.totalPaise, "₹")}</td></tr>`;
    })
    .join("");
  const kitRows = (document.kits ?? [])
    .map((kit) => {
      rowNumber += 1;
      const details = [
        ...(layout.showKitIdentity
          ? [
              `KIT · ${escapeHtml(kit.kitCode)} · ${escapeHtml(kit.packIdentity)}`,
            ]
          : []),
        ...(layout.showKitComponents
          ? [
              kit.components
                .map((component) => escapeHtml(component.name))
                .join(", "),
            ]
          : []),
      ]
        .filter(Boolean)
        .join(" · ");
      const batch = layout.showBatch
        ? `${escapeHtml(kit.kitBatchNumber)}${layout.showExpiry ? ` / ${expiry(kit.kitExpiryDate)}` : ""}`
        : "";
      return `<tr><td class="center">${rowNumber}</td><td class="description"><strong>${escapeHtml(kit.name)}</strong>${details ? `<div class="secondary">${details}</div>` : ""}</td>${layout.showHSN ? '<td class="center">-</td>' : ""}${layout.showBatch ? `<td class="center">${batch}</td>` : ""}${layout.showQuantity ? `<td class="qty">${quantity(kit.quantity)} ${escapeHtml(kit.uqc)}</td>` : ""}${layout.showMRP ? `<td class="num">${money(kit.printedMrpPaise, "₹")}</td>` : ""}<td class="num">${money(kit.unitPricePaise, "₹")}</td>${layout.showGST ? `<td class="num">${(kit.gstBps / 100).toFixed(2)}%</td>` : ""}<td class="num amount">${money(kit.totalPaise, "₹")}</td></tr>`;
    })
    .join("");
  const chargeRows = (document.charges ?? [])
    .map((charge) => {
      rowNumber += 1;
      const details = layout.showChargeDetails
        ? `<div class="secondary">${escapeHtml(charge.lineKind)} · ${escapeHtml(charge.chargeCode)}</div>`
        : "";
      return `<tr><td class="center">${rowNumber}</td><td class="description"><strong>${escapeHtml(charge.name)}</strong>${details}</td>${layout.showHSN ? '<td class="center">-</td>' : ""}${layout.showBatch ? '<td class="center">-</td>' : ""}${layout.showQuantity ? `<td class="qty">${quantity(charge.quantity)} ${escapeHtml(charge.uqc)}</td>` : ""}${layout.showMRP ? '<td class="num">-</td>' : ""}<td class="num">${money(charge.unitPricePaise, "₹")}</td>${layout.showGST ? `<td class="num">${(charge.gstBps / 100).toFixed(2)}%</td>` : ""}<td class="num amount">${money(charge.totalPaise, "₹")}</td></tr>`;
    })
    .join("");
  const header = invoiceHeaderHtml(document, layout);
  const tenderRows = layout.showTenders
    ? document.tenders
        .map(
          (tender) =>
            `<div><span>${escapeHtml(tender.method)}${tender.reference ? ` (${escapeHtml(tender.reference)})` : ""}</span><strong>${money(tender.amountPaise, "₹")}</strong></div>`,
        )
        .join("")
    : "";
  const taxRows = layout.showTaxBreakdown
    ? `<div><span>Taxable</span><strong>${money(document.totals.taxablePaise, "₹")}</strong></div><div><span>CGST</span><strong>${money(document.totals.cgstPaise, "₹")}</strong></div><div><span>SGST</span><strong>${money(document.totals.sgstPaise, "₹")}</strong></div><div><span>IGST</span><strong>${money(document.totals.igstPaise, "₹")}</strong></div>`
    : layout.showGST
      ? `<div><span>GST</span><strong>${money(document.totals.taxPaise, "₹")}</strong></div>`
      : "";
  const doctorInfo = layout.showDoctor ? invoiceDoctorInfoHtml(document) : "";
  const invoiceBandSections = [
    `<div class="bill-meta"><strong>TAX INVOICE</strong><div><strong>Bill No:</strong> ${escapeHtml(document.invoiceNumber)}</div><div><strong>Date:</strong> ${formatInvoiceDateTime(document.acceptedAt)}</div><div><strong>FY:</strong> ${escapeHtml(document.fiscalYear)}</div></div>`,
    ...(layout.showPatientInfo
      ? [
          `<div class="patient-info"><strong>Patient</strong><div>${escapeHtml(patient.name)}</div>${patient.phone ? `<div>${escapeHtml(patient.phone)}</div>` : ""}${patient.address ? `<div>${escapeHtml(patient.address)}</div>` : ""}</div>`,
        ]
      : []),
    ...(doctorInfo ? [`<div class="doctor-info">${doctorInfo}</div>`] : []),
  ];
  const footerLines = layout.showFooter
    ? `${layout.footerLines
        .map((line) => interpolateTemplateLine(line, document))
        .filter(Boolean)
        .map((line) => `<div>${escapeHtml(line)}</div>`)
        .join("")}${
        rendering
          ? `<div class="audit">${escapeHtml(rendering.templateVersion)} · ${escapeHtml(rendering.templateHash.slice(0, 12))}${rendering.printerName ? ` · ${escapeHtml(rendering.printerName)}` : ""}</div>`
          : ""
      }`
    : "";
  const headerCells = `<th class="center">S.No.</th><th>Description</th>${layout.showHSN ? '<th class="center">HSN</th>' : ""}${layout.showBatch ? `<th class="center">Batch${layout.showExpiry ? " / Exp" : ""}</th>` : ""}${layout.showQuantity ? '<th class="num">Qty</th>' : ""}${layout.showMRP ? '<th class="num">MRP</th>' : ""}<th class="num">Rate</th>${layout.showGST ? '<th class="num">GST%</th>' : ""}<th class="num">Amount</th>`;
  const columnWeights = [
    4,
    32,
    ...(layout.showHSN ? [9] : []),
    ...(layout.showBatch ? [16] : []),
    ...(layout.showQuantity ? [7] : []),
    ...(layout.showMRP ? [8] : []),
    8,
    ...(layout.showGST ? [7] : []),
    10,
  ];
  const totalColumnWeight = columnWeights.reduce(
    (total, weight) => total + weight,
    0,
  );
  const columnGroup = `<colgroup>${columnWeights
    .map(
      (weight) =>
        `<col style="width:${((weight / totalColumnWeight) * 100).toFixed(3)}%">`,
    )
    .join("")}</colgroup>`;
  const settlementDetails = layout.showTenders
    ? `<div class="tenders"><strong>Tenders</strong>${tenderRows}</div>`
    : "";

  return `<!doctype html><html><head><meta charset="utf-8"><title>Tax Invoice ${escapeHtml(document.invoiceNumber)}</title><style>
@page{size:${FANFOLD_9_5X5_5_PAGE.widthIn}in ${FANFOLD_9_5X5_5_PAGE.heightIn}in;margin:${layout.margins.top}mm ${layout.margins.right}mm ${layout.margins.bottom}mm ${layout.margins.left}mm}
*{box-sizing:border-box}
html,body{margin:0;padding:0;background:#fff;color:#000}
body{font:${layout.fontSize}pt/${layout.lineSpacing} ui-monospace,SFMono-Regular,Consolas,"Liberation Mono",monospace}
.form-header{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));border:1px solid #000;border-bottom:0;break-after:avoid}
.form-header.columns-2{grid-template-columns:repeat(2,minmax(0,1fr))}
.form-header.columns-1{grid-template-columns:1fr}
.form-header>div{min-width:0;padding:2px 4px;border-right:1px solid #000}
.form-header>div:last-child{border-right:0}
.store{text-align:left}
.store h1{font-size:${layout.fontSize * 1.7}pt;line-height:1;margin:0 0 1px}
.contact{text-align:center}
.regulatory{text-align:right}
.form-header.columns-1>div{text-align:center}
.form-header.columns-2>div:first-child{text-align:left}
.form-header.columns-2>div:last-child{text-align:right}
.invoice-band{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));border:1px solid #000;break-after:avoid}
.invoice-band.columns-2{grid-template-columns:repeat(2,minmax(0,1fr))}
.invoice-band.columns-1{grid-template-columns:1fr}
.invoice-band>div{min-width:0;padding:2px 4px;border-right:1px solid #000;overflow-wrap:anywhere}
.invoice-band>div:last-child{border-right:0}
.items{width:100%;border-collapse:collapse;table-layout:fixed;margin:0}
.items thead{display:table-header-group}
.items tr{break-inside:avoid;page-break-inside:avoid}
.items th,.items td{border:1px solid #000;padding:${layout.compactMode ? "1px 3px" : "2px 4px"};vertical-align:top;overflow-wrap:anywhere}
.items th{font-weight:700;text-align:left;white-space:nowrap}
.secondary,.qty-secondary{font-size:.82em;margin-top:0}
.center{text-align:center!important}
.qty{text-align:right!important}
.num{text-align:right!important;white-space:nowrap}
.amount{font-weight:700}
.end-block{break-inside:avoid;page-break-inside:avoid}
.settlement{display:grid;grid-template-columns:1fr 1fr;border:1px solid #000;border-top:0}
.settlement>div{padding:2px 4px}
.settlement-details{display:flex;flex-direction:column;border-right:1px solid #000}
.tenders>div,.summary>div,.line-count{display:flex;justify-content:space-between;gap:6px}
.line-count{margin-top:auto;border-top:1px solid #000;padding-top:2px}
.grand{border-top:2px solid #000;margin-top:1px;padding-top:2px;font-size:1.12em}
.form-footer{border:1px solid #000;border-top:0;padding:2px 4px;text-align:center}
.audit{font-size:.75em;margin-top:1px}
@media print{html,body{width:auto;height:auto}.items thead{display:table-header-group}}
</style></head><body>${header}<section class="invoice-band columns-${invoiceBandSections.length}">${invoiceBandSections.join("")}</section><table class="items">${columnGroup}<thead><tr>${headerCells}</tr></thead><tbody>${productRows}${kitRows}${chargeRows}</tbody></table><section class="end-block"><section class="settlement"><div class="settlement-details">${settlementDetails}<div class="line-count"><span>Total items</span><strong>${rowNumber}</strong></div></div><div class="summary"><div><span>Subtotal</span><strong>${money(document.totals.subtotalPaise, "₹")}</strong></div>${layout.showDiscount ? `<div><span>Discount</span><strong>-${money(document.totals.discountPaise, "₹")}</strong></div>` : ""}${taxRows}${document.totals.roundOffPaise ? `<div><span>Round-off</span><strong>${money(document.totals.roundOffPaise, "₹")}</strong></div>` : ""}<div class="grand"><span>TOTAL</span><strong>${money(document.totals.payablePaise, "₹")}</strong></div></div></section>${layout.showFooter && footerLines ? `<footer class="form-footer">${footerLines}</footer>` : ""}</section></body></html>`;
}

export function renderInvoiceWithTemplate(
  document: InvoicePrintDocument,
  format: InvoicePrintFormat,
  layout?: Partial<PrintLayoutConfig> | null,
  rendering?: InvoiceRenderingMetadata,
) {
  if (!layout) {
    if (format === "80MM") return renderInvoice80mm(document);
    if (format === "A4") return renderInvoiceA4(document);
  }
  const normalized = normalizeInvoiceLayout(format, layout);
  if (format === "DOT_MATRIX_9_5X5_5") {
    return configuredFanfoldInvoice(document, normalized, rendering);
  }
  return format === "58MM" || format === "80MM"
    ? configuredThermalInvoice(document, format, normalized)
    : configuredPageInvoice(document, format, normalized);
}

export function renderInvoice(
  document: InvoicePrintDocument,
  format: InvoicePrintFormat,
  layout?: Partial<PrintLayoutConfig> | null,
  rendering?: InvoiceRenderingMetadata,
) {
  return renderInvoiceWithTemplate(document, format, layout, rendering);
}

export function renderInvoiceEscPosBytes(
  document: InvoicePrintDocument,
  format: "58MM" | "80MM",
  layout?: Partial<PrintLayoutConfig> | null,
  options: { cutPaper?: boolean; openDrawer?: boolean } = {},
) {
  const text = renderInvoiceWithTemplate(document, format, layout);
  const encoded = new TextEncoder().encode(text);
  const prefix = new Uint8Array([0x1b, 0x40]);
  const drawer = options.openDrawer
    ? new Uint8Array([0x1b, 0x70, 0x00, 0x19, 0xfa])
    : new Uint8Array();
  const suffix =
    options.cutPaper === false
      ? new Uint8Array([0x1b, 0x64, 0x03])
      : new Uint8Array([0x1b, 0x64, 0x03, 0x1d, 0x56, 0x00]);
  const result = new Uint8Array(
    prefix.length + drawer.length + encoded.length + suffix.length,
  );
  result.set(prefix, 0);
  result.set(drawer, prefix.length);
  result.set(encoded, prefix.length + drawer.length);
  result.set(suffix, prefix.length + drawer.length + encoded.length);
  return result;
}

export function renderInvoiceHtmlArtifact(
  document: InvoicePrintDocument,
  format: InvoicePrintFormat,
  layout?: Partial<PrintLayoutConfig> | null,
  rendering?: InvoiceRenderingMetadata,
) {
  const normalized = normalizeInvoiceLayout(format, layout);
  const rendered = renderInvoiceWithTemplate(
    document,
    format,
    normalized,
    rendering,
  );
  if (rendered.trimStart().toLowerCase().startsWith("<!doctype html>")) {
    return rendered;
  }
  const pageWidth = format === "58MM" ? 58 : 80;
  const contentWidth = Math.max(
    20,
    pageWidth - normalized.margins.left - normalized.margins.right,
  );
  const family =
    normalized.fontFamily === "monospace"
      ? "ui-monospace,SFMono-Regular,Consolas,monospace"
      : normalized.fontFamily === "serif"
        ? "Georgia,serif"
        : "Arial,sans-serif";
  return `<!doctype html><html><head><meta charset="utf-8"><title>Invoice ${escapeHtml(document.invoiceNumber)}</title><style>@page{size:${pageWidth}mm auto;margin:${normalized.margins.top}mm ${normalized.margins.right}mm ${normalized.margins.bottom}mm ${normalized.margins.left}mm}html,body{margin:0;padding:0;background:#fff;color:#000}body{width:${contentWidth}mm;font:${normalized.fontSize}px/${normalized.lineSpacing} ${family}}pre{margin:0;white-space:pre-wrap;overflow-wrap:anywhere;font:inherit}</style></head><body><pre>${escapeHtml(rendered)}</pre></body></html>`;
}
